import {
    type LogLevel,
    type LogRecord,
    type Sink,
    configureSync,
    defaultConsoleFormatter,
    getConsoleSink,
} from '@logtape/logtape';

import { resolveTenancy } from '@/lib/log-context.ts';

/**
 * Configures LogTape for the app: one console sink, tenancy stamped on every record, and a root level
 * that follows the environment. Call sites take `getLogger` from `@logtape/logtape` directly; this module
 * only has to be imported once, for its side effect, before the first record is emitted.
 *
 * It is also the one place `console.*` is allowed - `no-console` is an ESLint error everywhere else.
 */

// Read straight from `import.meta.env` rather than through `@/utils/envUtils.ts`: that module reaches the
// config and notification stores, and evaluating them here would beat the logger they write to.
const isProd = import.meta.env.VITE_ENVIRONMENT === 'prod';

export const resolveRootLevel = (prod: boolean): LogLevel => (prod ? 'info' : 'debug');

const resolveTenancySafely = (): Record<string, unknown> => {
    try {
        return resolveTenancy();
    } catch {
        return {};
    }
};

/**
 * Resolved per record rather than bound per logger, because the selected customer changes long after
 * module evaluation, and stamped last so a call site cannot supply its own tenancy.
 */
export const withTenancy =
    (sink: Sink): Sink =>
    (record) =>
        sink({ ...record, properties: { ...record.properties, ...resolveTenancySafely() } });

/**
 * `defaultConsoleFormatter` renders the message and nothing else, so without appending the bag by hand
 * every property, tenancy included, is dropped before it reaches devtools.
 */
const consoleFormatter = (record: LogRecord): readonly unknown[] => [
    ...defaultConsoleFormatter(record),
    record.properties,
];

/** Exported so a test can assert the console sink is the tenancy-wrapped one, not a bare one. */
export const buildSinks = (): Record<string, Sink> => ({
    console: withTenancy(getConsoleSink({ formatter: consoleFormatter })),
});

let configured = false;

/** Never throws: a logging failure must not white-screen the app. */
export const initLogging = (): void => {
    if (configured) {
        return;
    }

    try {
        const sinks = buildSinks();

        // Synchronous, so nothing races the first render.
        configureSync({
            reset: true,
            sinks: sinks,
            loggers: [
                { category: [], sinks: Object.keys(sinks), lowestLevel: resolveRootLevel(isProd) },
                // LogTape's own diagnostics, kept off the app's level.
                {
                    category: ['logtape', 'meta'],
                    sinks: ['console'],
                    parentSinks: 'override',
                    lowestLevel: 'warning',
                },
            ],
        });

        configured = true;
    } catch (error) {
        // Logging is what failed, so this is the only channel left.
        console.error('LogTape init failed; continuing without structured logging', error);
    }
};

// Configured on import, ahead of the first record.
initLogging();
